import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import { Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { rules, CustomComponentCommonProps } from "../utils";
import Datepicker from "components/Input/Datepicker/Datepicker";
import CheckboxInput from "components/Input/CheckboxInput/CheckboxInput";

const TerminalDialogContent = ({
  action,
  onSubmit,
  setFormSubmitHandler,
  options,
  terminalEditTarget,
}) => {
  const { t } = useTranslation();
  const { control, handleSubmit, watch, reset } = useForm();
  const FORM_LABEL_KEY =
    "page.merchants.merchantDetail.maintenanceRecord.terminalDialog.fields";

  const handleInternalSubmit = async (data) => {
    try {
      await onSubmit(data, action); // Should trigger parent's `handleTerminalFormSubmit`
    } catch (error) {
      console.error("Submission error:", error);
    }
  };
  const formValue = watch();
  const dropdown = {
    installationDate: options?.installationDate?.data[0]?.install_date,
    maintenanceParties: options?.maintenanceParties.data,
    terminalModel: options?.terminalModel.data,
    serviceIntegratorOptions: options?.serviceIntegratorOptions,
    fullEmployeeList: options?.fullEmployeeList?.data,
    terminalList: options?.terminalList?.data,
  };

  useEffect(() => {
    if (setFormSubmitHandler) {
      const submitHandler = () => {
        handleSubmit((data) => {
          handleInternalSubmit(data);
        })();
      };
      setFormSubmitHandler(submitHandler);
    }
  }, [handleSubmit, handleInternalSubmit, setFormSubmitHandler]);

  useEffect(() => {
    if (action === "ADD") {
      reset({
        terminalType: "",
        terminalModel: "",
        employeeId: "",
        terminalSerialNumber: "",
        maintenancePartyId: "",
        serviceIntegratorId: "",
        installationDate: dropdown?.installationDate,
        remarks: "",
        isSpareTerminal: false,
      });
    } else if (terminalEditTarget && action === "UPDATE") {
      reset({
        terminalType: terminalEditTarget.terminalType,
        terminalModel: terminalEditTarget.terminalModel,
        employeeId: terminalEditTarget.employeeId,
        terminalSerialNumber: terminalEditTarget.terminalSerialNumber,
        maintenancePartyId: terminalEditTarget.maintenancePartyId,
        serviceIntegratorId: terminalEditTarget.serviceIntegratorId,
        installationDate: terminalEditTarget.installationDate,
        remarks: terminalEditTarget.remarks,
        isSpareTerminal: terminalEditTarget?.status === "SPARE",
      });
    }
  }, [terminalEditTarget]);

  const employeeOptions = dropdown.fullEmployeeList
    .filter((employee) => {
      const isAssigned = dropdown.terminalList.find(
        (t) => t.employeeId === employee.employeeId,
      );

      if (employee.employeeId === terminalEditTarget?.employeeId) {
        return true;
      }
      return employee.status !== "TERMINATED" && !isAssigned;
    })
    .map((employee) => ({
      key: employee.employeeId,
      value: employee.employeeId,
      title: `${employee.employeeId} - ${employee.firstname} ${employee.lastname}`,
    }));

  const typeOptions = Object.keys(dropdown?.terminalModel).map((key) => ({
    key: key,
    title: key,
    value: key,
  }));

  const generateOptions = (items) =>
    Object.entries(items).map(([key, value]) => ({
      key,
      value: key,
      title: value,
    }));

  const terminalOptions = generateOptions(
    dropdown.terminalModel.TERMINAL || {},
  );
  const accessoryOptions = generateOptions(
    dropdown.terminalModel.ACCESSORY || {},
  );

  const maintenancePartiesOptions = dropdown.maintenanceParties.map(
    (party) => ({
      key: party.company,
      value: party.id,
      title: party.company,
    }),
  );

  const serviceIntegratorOptions = dropdown.serviceIntegratorOptions.map(
    (integrator) => ({
      key: integrator.company,
      value: integrator.id,
      title: integrator.company,
    }),
  );

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleInternalSubmit)}>
        <Stack direction={"column"}>
          <Controller
            name="terminalType"
            control={control}
            defaultValue=""
            rules={rules({
              required: true,
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={typeOptions}
                sxParams={{ selectionError: !!error }}
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.terminalType`),
                  value,
                  "Selection",
                  true,
                  true,
                )}
              />
            )}
          />
          <Controller
            name="terminalModel"
            control={control}
            defaultValue=""
            rules={rules({
              required: true,
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={
                  formValue?.terminalType === "TERMINAL"
                    ? terminalOptions
                    : formValue?.terminalType === "ACCESSORY"
                      ? accessoryOptions
                      : []
                }
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.terminalModel`),
                  value,
                  "Selection",
                  true,
                  true,
                )}
                sxParams={{ selectionError: !!error }}
              />
            )}
          />
          {formValue.terminalType === "TERMINAL" &&
            !formValue.isSpareTerminal && (
              <Controller
                name="employeeId"
                control={control}
                defaultValue=""
                rules={rules({
                  required: true,
                })}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Selection
                    errorMessage={error?.message || ""}
                    onchangeFn={onChange}
                    options={employeeOptions}
                    {...CustomComponentCommonProps(
                      t(`${FORM_LABEL_KEY}.employeeId`),
                      value,
                      "Selection",
                      true,
                      true,
                    )}
                    sxParams={{ selectionError: !!error }}
                  />
                )}
              />
            )}

          <Controller
            name="terminalSerialNumber"
            control={control}
            defaultValue=""
            rules={rules({
              required: true,
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.terminalSerialNumber`),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                sxParams={{
                  textError: !!error,
                }}
              />
            )}
          />
          <Controller
            name="maintenancePartyId"
            control={control}
            defaultValue=""
            rules={rules({
              required: true,
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={maintenancePartiesOptions}
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.maintenancePartyId`),
                  value,
                  "Selection",
                  true,
                  true,
                )}
                sxParams={{ selectionError: !!error }}
              />
            )}
          />
          <Controller
            name="serviceIntegratorId"
            control={control}
            defaultValue=""
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={serviceIntegratorOptions}
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.serviceIntegratorId`),
                  value,
                  "Selection",
                  false,
                  true,
                )}
                sxParams={{ selectionError: !!error }}
              />
            )}
          />
          <Controller
            name="installationDate"
            control={control}
            defaultValue={dropdown?.installationDate}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Datepicker
                //
                errorMessage={error?.message || ""}
                type="start"
                onchangeFn={onChange}
                sxParams={{
                  datepickerError: !!error,
                }}
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.installationDate`),
                  value,
                  "Datepicker",
                  true,
                  true,
                )}
              />
            )}
          />
          <Controller
            name="remarks"
            control={control}
            defaultValue=""
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}.remarks`),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                sxParams={{
                  textError: !!error,
                }}
              />
            )}
          />
          {formValue.terminalType === "TERMINAL" && (
            <Controller
              name="isSpareTerminal"
              defaultValue={false}
              control={control}
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <CheckboxInput
                  label={t(`${FORM_LABEL_KEY}.isSpareTerminal`)}
                  oncheckFn={onChange}
                  checked={value}
                />
              )}
            />
          )}
        </Stack>
      </form>
    </Stack>
  );
};

export default React.memo(TerminalDialogContent);
